import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { VideoPrep } from '../api/focusOverlay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import {
  listAttachmentsForMySession,
  listAttachmentsForTenantSession,
  listMyCaseAttachments,
} from '../api/sessionAttachments';
import { useFormatTime } from '../util/displayTimeZone';
import { VideoClock } from './VideoClock';

/**
 * Video panel above the cognitive trace (MULTI-PARTICIPANT-FOCUS moment
 * inspector). Instead of a filmstrip of discrete frames, this plays the
 * participant's actual clip from S3: click a point on the trace and the
 * video seeks to that instant and plays (video_time = session_time − offset).
 * The chart is the scrubber. When a session has more than one camera the
 * clips stack vertically (each labelled + independently synced) so both
 * angles play at once. Audio-only fallback when a recording has no video.
 */

const CLIP_HALF_WINDOW_S = 5;

/** Play all: how often the shared clock advances and re-checks the clips. */
const PLAY_ALL_TICK_MS = 250;
/** Play all: a clip further than this from the shared clock is re-seeked. */
const PLAY_ALL_DRIFT_S = 0.6;
/** Play all: a seek on a clip's own controls further than this from the
 *  clock was the viewer's, so the clock follows it. */
const USER_SEEK_S = 1.5;
/** Smallest width a resized clip can be dragged down to (px). */
const MIN_RESIZED_WIDTH_PX = 160;
/** Keyboard step for the resize handle (px). */
const RESIZE_KEY_STEP_PX = 40;

interface VideoSrc {
  id: string;
  url: string;
  offset: number;
  camera: string;
}

/**
 * Human labels for the stacked clips: prefer the edge camera name, fall
 * back to the filename, and guarantee distinctness (two cameras can carry
 * the same name/filename — or none at all) by suffixing a positional index
 * on any collision so the operator can tell the angles apart.
 */
function cameraLabels(vids: VideoSrc[]): string[] {
  const bare = vids.map((v, i) => (v.camera || '').trim() || `Camera ${i + 1}`);
  const counts = new Map<string, number>();
  for (const label of bare) counts.set(label, (counts.get(label) ?? 0) + 1);
  return bare.map((label, i) =>
    (counts.get(label) ?? 0) > 1 ? `${label} (${i + 1})` : label,
  );
}

interface Props {
  /** Tenant-admin fetch. When null/absent (and not a case-scoped participant
   *  view), the media is fetched via the participant's own-session endpoint —
   *  used on the personal Dashboard. */
  tenantId?: string | null;
  sessionRowId: string;
  participantName: string;
  /** Session-elapsed seconds picked on the trace, or null. */
  selectedTime: number | null;
  startedAt: string;
  /** Participant read-only: fetch via the case-scoped /me endpoint instead of
   *  the tenant one. Requires `instanceId`. */
  asParticipant?: boolean;
  instanceId?: string;
  /** Video-prep progress from the overlay payload; when set, the clip isn't
   *  ready yet and we show a progress bar instead of "No video". */
  videoPrep?: VideoPrep | null;
  /**
   * Force silence. Set on every strip but one when a case shows several
   * participants side by side — two soundtracks playing over each other is
   * noise, and the anchor audio of the person you're NOT watching is
   * actively misleading. The muted strips still seek and play in step.
   */
  muted?: boolean;
  /**
   * Optional ceiling on a clip's rendered height. Left unset the clip scales
   * to the width it has been given — which is what you want on a wide screen,
   * where a fixed cap left a 16:9 video spanning ~420px of a ~745px column
   * and the rest empty. The default ceiling is a share of the VIEWPORT, not a
   * fixed pixel count, so the panel grows with the window but a portrait clip
   * still can't push the trace off the bottom of the screen.
   */
  maxVideoHeight?: number | string;
  /** Play all: the session-elapsed second the shared clock stops at (the end
   *  of the trace). Unset → it runs until paused. */
  endT?: number;
  /** Play all: the shared clock's session-elapsed time, a few times a
   *  second while playing (and once more when it stops at `endT`). */
  onPlayheadTime?: (t: number) => void;
  /** Play all started (true) or stopped (false) — by the button, the end of
   *  the trace, or the viewer pausing a clip on its own controls. */
  onPlayAllChange?: (playing: boolean) => void;
}

export interface MediaStripHandle {
  /**
   * Second click on the moment you're already on: stop if anything is
   * playing, otherwise replay from that moment. Returns whether it is
   * playing afterwards.
   *
   * Imperative on purpose. Playback state lives in the media elements
   * themselves — the viewer can also hit pause on the <video> controls — so a
   * `playing` prop mirrored in the parent would drift out of sync with what
   * the user is actually hearing. The trace just asks "toggle", and this
   * component answers from the elements' real state.
   */
  togglePlayback: () => boolean;
  /**
   * Play all: every camera and the session audio together from session
   * second `fromT`, on one shared clock that `onPlayheadTime` reports. A
   * camera whose clip starts later joins when the clock reaches it; one
   * that has ended stops. Runs with no media at all too, so the trace still
   * replays.
   */
  playAll: (fromT: number) => void;
  /** Stop Play all where it is. */
  pauseAll: () => void;
}

export const MediaStrip = forwardRef<MediaStripHandle, Props>(function MediaStrip(
  {
    tenantId,
    sessionRowId,
    participantName,
    selectedTime,
    startedAt,
    asParticipant = false,
    instanceId,
    videoPrep = null,
    muted: forceMuted = false,
    maxVideoHeight,
    endT,
    onPlayheadTime,
    onPlayAllChange,
  },
  ref,
) {
  const fmtTime = useFormatTime();
  const [videos, setVideos] = useState<VideoSrc[] | null>(null);
  // Bumped when video-prep finishes, to refetch attachments and pick up the
  // now-ready clip.
  const [reloadKey, setReloadKey] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioOffset, setAudioOffset] = useState(0);
  const [playingAudio, setPlayingAudio] = useState(false);
  // One <video> element per camera, keyed by attachment id, plus a per-clip
  // pending-seek stash (applied on that clip's loadedmetadata).
  const videoEls = useRef<Map<string, HTMLVideoElement>>(new Map());
  const pendingSeeks = useRef<Map<string, number>>(new Map());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const clipEndRef = useRef(0);
  // Play all: the shared clock (session-elapsed seconds at a moment in
  // performance.now() time), or null when not playing all.
  const clockRef = useRef<{ t: number; ms: number } | null>(null);
  const tickRef = useRef<number | null>(null);
  // A clip the viewer has resized: its width in px, keyed by attachment id.
  const [widths, setWidths] = useState<Record<string, number>>({});
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    setVideos(null);
    setAudioUrl(null);
    setAudioOffset(0);
    setPlayingAudio(false);
    pendingSeeks.current.clear();
    setWidths({});
    (asParticipant && instanceId
      ? listMyCaseAttachments(instanceId, sessionRowId)
      : tenantId
        ? listAttachmentsForTenantSession(tenantId, sessionRowId)
        : listAttachmentsForMySession(sessionRowId))
      .then((rows) => {
        if (!live) return;
        const vids: VideoSrc[] = rows
          .filter((r) => r.kind === 'video' && r.presigned_get_url)
          .map((r) => ({
            id: r.id,
            url: r.presigned_get_url as string,
            offset: r.audio_sync_offset_s ?? 0,
            camera: r.camera_name || r.original_filename || 'Camera',
          }));
        setVideos(vids);
        const audio =
          rows.find((r) => r.kind === 'audio_session_anchor' && r.presigned_get_url) ??
          rows.find((r) => r.kind === 'audio' && r.presigned_get_url);
        setAudioUrl(audio?.presigned_get_url ?? null);
        setAudioOffset(audio?.audio_sync_offset_s ?? 0);
      })
      .catch(() => live && setVideos([]));
    return () => {
      live = false;
    };
  }, [tenantId, sessionRowId, asParticipant, instanceId, reloadKey]);

  // When video-prep finishes (videoPrep goes present → null), refetch so the
  // now-ready clip replaces the progress bar.
  const wasPreparing = useRef(false);
  useEffect(() => {
    const preparing = videoPrep !== null;
    if (wasPreparing.current && !preparing) setReloadKey((k) => k + 1);
    wasPreparing.current = preparing;
  }, [videoPrep]);

  const labels = useMemo(() => cameraLabels(videos ?? []), [videos]);
  // The primary clip (first camera) drives the shared anchor soundtrack; the
  // others play muted alongside it, all synced to the same session timeline.
  const primary = videos && videos.length ? videos[0] : null;

  // Seek (and play) every camera whenever a moment is pinned on the trace —
  // each clip to its own offset, so the angles stay aligned to the session.
  //
  // A clip that does not COVER the pinned moment is paused instead, because a
  // session can now hold several clips from the SAME camera. An appliance
  // restarted mid-case leaves a salvaged partial plus a resumed clip, and they
  // cover different stretches of the session — e.g. 0–100 s and 99–124 s.
  //
  // Clamping with Math.max(0, …) was fine when a camera meant one clip. With
  // two it actively lies: pinning t=20 s clamped the resumed clip to 0 and
  // played it, showing footage from 1:39 while the trace said 0:20. Pinning
  // past a clip's end seeked beyond it and froze on its last frame. Either way
  // the operator is shown the wrong moment with nothing saying so.
  useEffect(() => {
    if (selectedTime == null || !videos) return;
    // A moment picked during Play all moves the shared clock there.
    if (clockRef.current) {
      clockRef.current = { t: selectedTime, ms: performance.now() };
    }
    for (const v of videos) {
      const el = videoEls.current.get(v.id);
      const t = selectedTime - v.offset;
      // Before this clip starts → it has nothing to show for this moment.
      if (t < 0) {
        if (el) el.pause();
        pendingSeeks.current.delete(v.id);
        continue;
      }
      // Past its end → likewise. Duration is only known once metadata has
      // loaded; until then fall through and let the pending seek decide, so a
      // slow-loading clip still lands on the right frame.
      if (el && el.readyState >= 1) {
        if (Number.isFinite(el.duration) && t > el.duration) {
          el.pause();
          continue;
        }
        seekPlay(el, t);
      } else {
        pendingSeeks.current.set(v.id, t);
      }
    }
  }, [selectedTime, videos]);

  function seekPlay(el: HTMLVideoElement, t: number) {
    try {
      el.currentTime = t;
    } catch {
      /* not seekable yet */
    }
    const p = el.play() as Promise<void> | undefined;
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  function onLoadedMeta(id: string) {
    const el = videoEls.current.get(id);
    const t = pendingSeeks.current.get(id);
    if (el && t != null) {
      pendingSeeks.current.delete(id);
      // Same coverage rule as above — this is where a clip that was still
      // loading when the moment was pinned finally learns its own duration.
      if (Number.isFinite(el.duration) && t > el.duration) {
        el.pause();
        return;
      }
      seekPlay(el, t);
    }
  }

  // Anchor as the videos' shared SOUNDTRACK. Edge/ORDRO clips have no audio
  // track, so when video is present alongside the session anchor audio (the
  // capture-app mic), play the anchor synced to the PRIMARY clip's timeline —
  // the videos are muted (silent anyway) and the anchor follows the primary.
  // session_t = video_t + videoOffset; anchor_t = session_t − anchorOffset.
  // A silenced strip never plays the anchor either — it is the loudest part.
  const anchorSoundtrack = !!(primary && audioUrl) && !forceMuted;
  function syncAnchorToVideo(shouldPlay: boolean) {
    // Play all drives the soundtrack from its own clock (see applyClock).
    if (clockRef.current) return;
    const v = primary ? videoEls.current.get(primary.id) : null;
    const a = audioRef.current;
    if (!v || !a || !anchorSoundtrack) return;
    const target = Math.max(0, v.currentTime + (primary?.offset ?? 0) - audioOffset);
    if (Math.abs(a.currentTime - target) > 0.25) {
      try {
        a.currentTime = target;
      } catch {
        /* not seekable yet */
      }
    }
    if (shouldPlay && a.paused) {
      const p = a.play() as Promise<void> | undefined;
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else if (!shouldPlay && !a.paused) {
      a.pause();
    }
  }

  // Audio fallback (no video): play a ±window around the pinned moment.
  function playAudioAt(sessionT: number) {
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    const audioT = Math.max(0, sessionT - audioOffset);
    clipEndRef.current = audioT + CLIP_HALF_WINDOW_S;
    try {
      el.currentTime = Math.max(0, audioT - CLIP_HALF_WINDOW_S);
    } catch {
      /* noop */
    }
    const p = el.play() as Promise<void> | undefined;
    if (p && typeof p.catch === 'function') p.catch(() => setPlayingAudio(false));
    setPlayingAudio(true);
  }
  function stopAudio() {
    audioRef.current?.pause();
    setPlayingAudio(false);
  }
  /** True while any clip or the anchor audio is actually running. Read from
   *  the elements rather than a state flag so a manual pause on the video
   *  controls counts. */
  const isPlaying = useCallback(() => {
    for (const el of videoEls.current.values()) {
      if (!el.paused && !el.ended) return true;
    }
    const a = audioRef.current;
    return !!(a && !a.paused && !a.ended);
  }, []);

  const pauseEverything = useCallback(() => {
    for (const el of videoEls.current.values()) el.pause();
    // Pausing the primary video already stops the anchor soundtrack via its
    // onPause; this also covers the audio-only path.
    audioRef.current?.pause();
    setPlayingAudio(false);
  }, []);

  // ── Play all ──────────────────────────────────────────────────────────
  // What the clock's interval needs, read fresh on every tick.
  const live = useRef({ videos, audioUrl, audioOffset, anchorSoundtrack, forceMuted });
  live.current = { videos, audioUrl, audioOffset, anchorSoundtrack, forceMuted };
  const callbacks = useRef({ endT, onPlayheadTime, onPlayAllChange });
  callbacks.current = { endT, onPlayheadTime, onPlayAllChange };

  /** Does clip `v` have footage for session second `t`? Before its start →
   *  no; past its end → no once its duration is known. */
  function covers(v: VideoSrc, el: HTMLVideoElement | undefined, t: number) {
    const tv = t - v.offset;
    if (tv < 0) return false;
    return !(el && el.readyState >= 1 && Number.isFinite(el.duration) && tv > el.duration);
  }

  /** Put every clip and the soundtrack where the shared clock says. */
  function applyClock(t: number) {
    const { videos: vids, audioUrl: aUrl, audioOffset: aOff, anchorSoundtrack: anchor, forceMuted: silent } =
      live.current;
    for (const v of vids ?? []) {
      const el = videoEls.current.get(v.id);
      if (!el) continue;
      const tv = t - v.offset;
      if (covers(v, el, t)) {
        if (el.paused) seekPlay(el, tv);
        else if (Math.abs(el.currentTime - tv) > PLAY_ALL_DRIFT_S) {
          try {
            el.currentTime = tv;
          } catch {
            /* not seekable yet */
          }
        }
      } else if (!el.paused) {
        el.pause();
      }
    }
    const a = audioRef.current;
    const wantsAudio = !!aUrl && !silent && (anchor || !(vids && vids.length));
    if (!a || !wantsAudio) return;
    const ta = t - aOff;
    const audioCovers =
      ta >= 0 && !(Number.isFinite(a.duration) && a.duration > 0 && ta > a.duration);
    if (!audioCovers) {
      if (!a.paused) a.pause();
      return;
    }
    if (Math.abs(a.currentTime - ta) > PLAY_ALL_DRIFT_S) {
      try {
        a.currentTime = ta;
      } catch {
        /* not seekable yet */
      }
    }
    if (a.paused) {
      const p = a.play() as Promise<void> | undefined;
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }

  const stopAll = useCallback(() => {
    if (!clockRef.current) return;
    clockRef.current = null;
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    tickRef.current = null;
    for (const el of videoEls.current.values()) el.pause();
    audioRef.current?.pause();
    setPlayingAudio(false);
    callbacks.current.onPlayAllChange?.(false);
  }, []);

  function tick() {
    const c = clockRef.current;
    if (!c) return;
    const now = performance.now();
    // Hold the clock while a clip that should be playing is still buffering,
    // so it isn't re-seeked over and over on a slow connection.
    let stalled = false;
    for (const v of live.current.videos ?? []) {
      const el = videoEls.current.get(v.id);
      if (el && !el.paused && covers(v, el, c.t) && el.readyState < 3) stalled = true;
    }
    const t = stalled ? c.t : c.t + (now - c.ms) / 1000;
    clockRef.current = { t, ms: now };
    const end = callbacks.current.endT;
    if (end !== undefined && t >= end) {
      callbacks.current.onPlayheadTime?.(end);
      stopAll();
      return;
    }
    applyClock(t);
    callbacks.current.onPlayheadTime?.(t);
  }
  const tickFn = useRef(tick);
  tickFn.current = tick;

  const startAll = useCallback((fromT: number) => {
    const t = Math.max(0, fromT);
    clockRef.current = { t, ms: performance.now() };
    // Start the media inside the click that asked for it, so the browser
    // lets the soundtrack play.
    applyClock(t);
    callbacks.current.onPlayheadTime?.(t);
    callbacks.current.onPlayAllChange?.(true);
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    tickRef.current = window.setInterval(() => tickFn.current(), PLAY_ALL_TICK_MS);
    // applyClock reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A different recording, or leaving the page, ends Play all.
  useEffect(() => stopAll, [sessionRowId, stopAll]);

  /** The viewer used a clip's own controls during Play all. A pause on a
   *  clip that should be playing stops everything; a seek far from the clock
   *  moves the clock there. */
  function onClipPause(v: VideoSrc) {
    const c = clockRef.current;
    const el = videoEls.current.get(v.id);
    if (!c || !el || el.ended) return;
    if (covers(v, el, c.t)) stopAll();
  }
  function onClipSeeked(v: VideoSrc) {
    const c = clockRef.current;
    const el = videoEls.current.get(v.id);
    if (!c || !el) return;
    const t = el.currentTime + v.offset;
    if (Math.abs(t - c.t) > USER_SEEK_S) clockRef.current = { t, ms: performance.now() };
  }

  // ── Resize ────────────────────────────────────────────────────────────
  /** Widest a clip may grow: the whole row. */
  const maxWidth = () => rowRef.current?.clientWidth || Number.POSITIVE_INFINITY;
  const setWidth = (id: string, w: number) =>
    setWidths((prev) => ({
      ...prev,
      [id]: Math.round(Math.min(maxWidth(), Math.max(MIN_RESIZED_WIDTH_PX, w))),
    }));
  const resetWidth = (id: string) =>
    setWidths((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /** Drag the top-right corner: right and up make the clip bigger, left
   *  and down smaller. The clip keeps its aspect ratio. */
  function startResize(id: string, e: ReactPointerEvent<HTMLElement>) {
    const el = videoEls.current.get(id);
    if (!el) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture?.(e.pointerId);
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = el.getBoundingClientRect().width;
    const move = (ev: PointerEvent) =>
      setWidth(id, startW + (ev.clientX - startX) - (ev.clientY - startY));
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  }
  function resizeByKey(id: string, e: ReactKeyboardEvent<HTMLElement>) {
    const grow = e.key === 'ArrowUp' || e.key === 'ArrowRight';
    const shrink = e.key === 'ArrowDown' || e.key === 'ArrowLeft';
    if (!grow && !shrink) return;
    e.preventDefault();
    const el = videoEls.current.get(id);
    const current = widths[id] ?? el?.getBoundingClientRect().width ?? 0;
    setWidth(id, current + (grow ? RESIZE_KEY_STEP_PX : -RESIZE_KEY_STEP_PX));
  }

  useImperativeHandle(
    ref,
    () => ({
      playAll: (fromT: number) => startAll(fromT),
      pauseAll: () => stopAll(),
      togglePlayback: () => {
        if (clockRef.current) {
          stopAll();
          return false;
        }
        if (isPlaying()) {
          pauseEverything();
          return false;
        }
        if (selectedTime == null) return false;
        if (videos && videos.length) {
          for (const v of videos) {
            const el = videoEls.current.get(v.id);
            if (el) seekPlay(el, Math.max(0, selectedTime - v.offset));
          }
          return true;
        }
        if (audioUrl) {
          playAudioAt(selectedTime);
          return true;
        }
        return false;
      },
    }),
    // `seekPlay` / `playAudioAt` are stable function declarations in this
    // component body; the data they act on is in the deps.
    [ref, isPlaying, pauseEverything, selectedTime, videos, audioUrl, startAll, stopAll],
  );

  const onAudioTime = () => {
    const el = audioRef.current;
    // Soundtrack mode (a video is present): the video drives the anchor, so
    // don't apply the ±window auto-stop used by the no-video fallback.
    if (!el || primary || clockRef.current) return;
    if (el.currentTime >= clipEndRef.current) stopAudio();
  };

  const atCaption =
    selectedTime == null
      ? 'click a point on the trace to jump the video there'
      : `@ ${fmtTime(
          new Date(Date.parse(startedAt) + selectedTime * 1000),
        )} · click the same point again to stop`;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1, bgcolor: 'background.default' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '.06em' }}>
          VIDEO · {participantName}
        </Typography>
        {anchorSoundtrack && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            · audio: session anchor
          </Typography>
        )}
        {videos && videos.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            · {videos.length} cameras
          </Typography>
        )}
        {!videos?.length && audioUrl && (
          <Tooltip title={playingAudio ? 'Stop' : 'Play audio at the picked moment'}>
            <span>
              <IconButton
                size="small"
                disabled={selectedTime == null}
                onClick={() => (playingAudio ? stopAudio() : playAudioAt(selectedTime ?? 0))}
              >
                {playingAudio ? <StopIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Typography variant="caption" color="text.secondary">
          {atCaption}
        </Typography>
      </Stack>

      {videos === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : videos.length ? (
        // Cameras side by side, not stacked: two angles on one recording are
        // meant to be compared at the same instant, and stacked they push the
        // second one (and everything below the panel) off the screen. Only a
        // phone is too narrow to carry both.
        <Stack
          ref={rowRef}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="flex-start"
        >
          {videos.map((v, i) => {
            const isPrimary = v.id === primary?.id;
            // Only the primary clip drives the anchor soundtrack; the others
            // always play muted so we don't stack multiple audio tracks.
            const muted = forceMuted || anchorSoundtrack || !isPrimary;
            const resized = widths[v.id];
            return (
              <Box
                key={v.id}
                sx={
                  resized
                    ? { flex: '0 0 auto', width: resized, maxWidth: '100%' }
                    : { flex: 1, minWidth: { xs: 0, sm: 200 }, width: '100%' }
                }
              >
                {videos.length > 1 && (
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.25 }}
                  >
                    {labels[i]}
                  </Typography>
                )}
                <Box
                  sx={{
                    position: 'relative',
                    lineHeight: 0,
                    width: resized ? '100%' : 'fit-content',
                    maxWidth: '100%',
                    mx: 'auto',
                    // The resize corner shows on hover / keyboard focus only.
                    '&:hover .media-resize, & .media-resize:focus-visible': { opacity: 1 },
                  }}
                >
                <VideoClock
                  getVideo={() => videoEls.current.get(v.id)}
                  startedAt={startedAt}
                  offsetS={v.offset}
                />
                <Box
                  component="video"
                  ref={(el: HTMLVideoElement | null) => {
                    if (el) videoEls.current.set(v.id, el);
                    else videoEls.current.delete(v.id);
                  }}
                  src={v.url}
                  controls
                  // Muted so the anchor is the single audio source (the clip is
                  // silent anyway) and so seek-on-click autoplay isn't blocked.
                  // The anchor <audio> follows the primary video.
                  muted={muted}
                  onPlay={isPrimary ? () => syncAnchorToVideo(true) : undefined}
                  onPause={() => {
                    onClipPause(v);
                    if (isPrimary) syncAnchorToVideo(false);
                  }}
                  onSeeked={() => {
                    onClipSeeked(v);
                    if (isPrimary) syncAnchorToVideo(!videoEls.current.get(v.id)?.paused);
                  }}
                  onTimeUpdate={
                    isPrimary
                      ? () => {
                          if (anchorSoundtrack && !videoEls.current.get(v.id)?.paused) {
                            syncAnchorToVideo(true);
                          }
                        }
                      : undefined
                  }
                  preload="metadata"
                  onLoadedMetadata={() => onLoadedMeta(v.id)}
                  data-testid={i === 0 ? 'moment-video' : `moment-video-${i}`}
                  sx={{
                    // Intrinsic size, scaled down to fit BOTH constraints, so
                    // the element always matches the clip's own aspect — no
                    // letterboxing to paint. Width binds on a wide screen
                    // (the clip fills its column), height binds on a short
                    // one or for a portrait clip.
                    width: resized ? '100%' : 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    // Several clips share the row, so their width binds long
                    // before this does — the ceiling is only there to stop a
                    // portrait clip running off the screen. A LONE clip is the
                    // opposite case: unbounded it would fill the whole column
                    // and push the trace below the fold, so it gets a tighter
                    // one. Both scale with the window rather than being a
                    // fixed pixel count.
                    maxHeight: resized
                      ? 'none'
                      : (maxVideoHeight ??
                        (videos.length > 1 ? '52vh' : 'min(360px, 40vh)')),
                    borderRadius: 1,
                    display: 'block',
                    margin: '0 auto',
                    bgcolor: '#000',
                    objectFit: 'contain',
                  }}
                />
                <Tooltip title="Drag to resize · double-click to reset">
                  <Box
                    component="button"
                    type="button"
                    className="media-resize"
                    aria-label={`Resize ${labels[i]}`}
                    data-testid={i === 0 ? 'video-resize' : `video-resize-${i}`}
                    onPointerDown={(e: ReactPointerEvent<HTMLElement>) => startResize(v.id, e)}
                    onDoubleClick={() => resetWidth(v.id)}
                    onKeyDown={(e: ReactKeyboardEvent<HTMLElement>) => resizeByKey(v.id, e)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 22,
                      height: 22,
                      p: 0,
                      border: 0,
                      borderTopRightRadius: 4,
                      borderBottomLeftRadius: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      cursor: 'nesw-resize',
                      touchAction: 'none',
                      opacity: resized ? 1 : 0,
                      transition: 'opacity .15s',
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <OpenInFullIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : videoPrep ? (
        <Box sx={{ py: 1.5, px: 0.5 }} data-testid="video-preparing">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Preparing video · {videoPrep.stage}
              {videoPrep.total
                ? ` (${videoPrep.done ?? 0}/${videoPrep.total})`
                : '…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={videoPrep.total ? 'determinate' : 'indeterminate'}
            value={
              videoPrep.total
                ? Math.min(
                    100,
                    Math.round(((videoPrep.done ?? 0) / videoPrep.total) * 100),
                  )
                : undefined
            }
          />
        </Box>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5, px: 0.5, color: 'text.secondary' }}>
          <VideocamOffIcon fontSize="small" />
          <Typography variant="body2">
            No video for this recording{audioUrl ? ' — audio only.' : '.'}
          </Typography>
        </Stack>
      )}

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={onAudioTime}
          preload={anchorSoundtrack ? 'auto' : 'none'}
        />
      )}
    </Box>
  );
});
